import { v4 as uuidv4 } from "uuid";

/**
 * The vertical that owns a funnel. Each vertical has its own funnel slot in
 * sessionStorage, so a funnel started by one vertical is never attributed to,
 * nor replaced by, another vertical's — a traveller can detour through flights
 * or cars and come back to the same lodging funnel their offers were shown
 * under.
 */
export type FunnelVertical = "air" | "lodging" | "cars";

const FUNNEL_ID_STORAGE_KEY_PREFIX = "funnel_id";
const FUNNEL_SEARCH_STORAGE_KEY_PREFIX = "funnel_search";

// Keys used before funnel storage was scoped per vertical: a single funnel ID
// plus the vertical that owned it. Read once to migrate sessions that were
// mid-funnel when the per-vertical keys shipped, so their booking is not minted
// under a fresh funnel that has no offers.
const LEGACY_FUNNEL_ID_STORAGE_KEY = "funnel_id";
const LEGACY_FUNNEL_VERTICAL_STORAGE_KEY = "funnel_vertical";

const funnelIdStorageKey = (vertical: FunnelVertical): string =>
  `${FUNNEL_ID_STORAGE_KEY_PREFIX}:${vertical}`;

const funnelSearchStorageKey = (vertical: FunnelVertical): string =>
  `${FUNNEL_SEARCH_STORAGE_KEY_PREFIX}:${vertical}`;

// Structural equality for normalized search params. Both sides are plain JSON
// (strings/numbers/booleans/arrays/objects) produced by the same normalization,
// so a small recursive comparison is enough — no deep-equality dependency
// needed. Object key order is ignored; array element order is significant.
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) {
    return true;
  }
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => deepEqual(item, b[index]))
    );
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  return (
    aKeys.length === bKeys.length &&
    aKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(bRecord, key) &&
        deepEqual(aRecord[key], bRecord[key])
    )
  );
};

/**
 * Moves a funnel stored under the pre-vertical keys into `vertical`'s slot when
 * that vertical owned it. Returns the migrated funnel ID, or `undefined` when
 * there is nothing to migrate for this vertical. A legacy funnel owned by
 * another vertical is left in place for that vertical to claim.
 */
const migrateLegacyFunnel = (vertical: FunnelVertical): string | undefined => {
  const legacyFunnelId = sessionStorage.getItem(LEGACY_FUNNEL_ID_STORAGE_KEY);
  if (
    legacyFunnelId === null ||
    sessionStorage.getItem(LEGACY_FUNNEL_VERTICAL_STORAGE_KEY) !== vertical
  ) {
    return undefined;
  }
  sessionStorage.setItem(funnelIdStorageKey(vertical), legacyFunnelId);
  sessionStorage.removeItem(LEGACY_FUNNEL_ID_STORAGE_KEY);
  sessionStorage.removeItem(LEGACY_FUNNEL_VERTICAL_STORAGE_KEY);
  return legacyFunnelId;
};

/**
 * Returns `vertical`'s current funnel ID, or `undefined` when that vertical
 * has no funnel — a caller attaching attribution must never report another
 * vertical's funnel. `undefined` (not `null`) so the result can be assigned
 * directly to optional request fields (`funnelId?: string`).
 */
export const getFunnelId = (vertical: FunnelVertical): string | undefined => {
  try {
    return (
      sessionStorage.getItem(funnelIdStorageKey(vertical)) ??
      migrateLegacyFunnel(vertical)
    );
  } catch {
    return undefined;
  }
};

/**
 * Returns the normalized search `vertical`'s current funnel was started with,
 * or `undefined` when there is no funnel or its search was not recorded (e.g.
 * a funnel migrated from the pre-vertical keys).
 */
export const getFunnelSearch = (vertical: FunnelVertical): unknown => {
  try {
    if (getFunnelId(vertical) === undefined) {
      return undefined;
    }
    const search = sessionStorage.getItem(funnelSearchStorageKey(vertical));
    return search === null ? undefined : JSON.parse(search);
  } catch {
    return undefined;
  }
};

export const shouldStartNewFunnel = <T>(
  currentSearchParams: T | undefined,
  nextSearchParams: T
): boolean => {
  return (
    currentSearchParams === undefined ||
    !deepEqual(currentSearchParams, nextSearchParams)
  );
};

const clearFunnel = (vertical: FunnelVertical): void => {
  try {
    sessionStorage.removeItem(funnelIdStorageKey(vertical));
    sessionStorage.removeItem(funnelSearchStorageKey(vertical));
  } catch {
    // sessionStorage itself is unavailable — there is nothing to clean up.
  }
};

/**
 * Makes `funnelId` the current funnel of `vertical`, replacing only that
 * vertical's funnel. `search` is the normalized search the funnel is for; it is
 * recorded alongside the ID so a later search committed from a surface with no
 * search of its own (e.g. the home page) can be compared against it.
 */
const storeFunnel = (
  vertical: FunnelVertical,
  funnelId: string,
  search?: unknown
): string | null => {
  try {
    const serializedSearch =
      search === undefined ? null : JSON.stringify(search);
    sessionStorage.setItem(funnelIdStorageKey(vertical), funnelId);
    if (serializedSearch === null) {
      sessionStorage.removeItem(funnelSearchStorageKey(vertical));
    } else {
      sessionStorage.setItem(
        funnelSearchStorageKey(vertical),
        serializedSearch
      );
    }
    return funnelId;
  } catch {
    // We could not persist the funnel ID (e.g. sessionStorage is unavailable).
    // Clear the vertical's existing funnel so a materially different search is
    // never attributed to the previous funnel's ID.
    clearFunnel(vertical);
    return null;
  }
};

/**
 * Starts a new funnel for `vertical`, replacing only that vertical's current
 * funnel. See `storeFunnel` for what `search` is for.
 */
export const startFunnel = (
  vertical: FunnelVertical,
  search?: unknown
): string | null => {
  try {
    return storeFunnel(vertical, uuidv4(), search);
  } catch {
    // A UUID could not be generated. Same reasoning as the persist failure in
    // `storeFunnel`: never leave a stale funnel for the new search to inherit.
    clearFunnel(vertical);
    return null;
  }
};

/**
 * Rotates `vertical`'s funnel ID when a committed search differs materially
 * from the current one. Funnel tracking is an analytics side-effect and must
 * never block navigation, so callers pass already-normalized search params and
 * set `nextSearch` to `undefined` when it could not be derived (e.g. a schema
 * parse failed). In that case the current funnel is left untouched.
 *
 * `currentSearch` is the search the caller's URL carries. When the URL carries
 * none (an entry surface such as the home page), the search the funnel was
 * started with is compared instead, so re-submitting the search a funnel is
 * already open for keeps that funnel — and the offers shown under it — instead
 * of minting a new one.
 */
export const maybeStartFunnel = <T>(
  vertical: FunnelVertical,
  currentSearch: T | undefined,
  nextSearch: T | undefined
): void => {
  if (nextSearch === undefined) {
    return;
  }
  if (getFunnelId(vertical) === undefined) {
    startFunnel(vertical, nextSearch);
    return;
  }
  const referenceSearch =
    currentSearch === undefined
      ? (getFunnelSearch(vertical) as T | undefined)
      : currentSearch;
  if (shouldStartNewFunnel(referenceSearch, nextSearch)) {
    startFunnel(vertical, nextSearch);
  }
};

/**
 * Seeds a funnel when landing on a search-results page that already has a
 * search but no active funnel for `vertical` — e.g. a deep link straight into
 * the results page, where no search is ever committed through
 * `maybeStartFunnel`. Callers pass the already-normalized current search;
 * `undefined` means "not a results page" (an entry surface with no route
 * search), so nothing is seeded. A funnel already owned by `vertical` is never
 * clobbered, so it is safe to call on every render/mount.
 *
 * `inheritedFunnelId` is the funnel of the search this page was opened from,
 * handed over by the opening tab (on desktop the hotel list opens the shop in
 * a new tab, whose sessionStorage starts empty). When given, the page adopts it
 * instead of minting its own, so every property opened from one search stays
 * attributed to that search.
 */
export const ensureFunnel = (
  vertical: FunnelVertical,
  currentSearch: unknown | undefined,
  inheritedFunnelId?: string
): void => {
  if (currentSearch === undefined || getFunnelId(vertical) !== undefined) {
    return;
  }
  if (inheritedFunnelId === undefined) {
    startFunnel(vertical, currentSearch);
  } else {
    storeFunnel(vertical, inheritedFunnelId, currentSearch);
  }
};
